import { useCallback, useEffect, useState } from 'react';
import { LiteUser } from '@client/new/common/LiteUser.mapper';
import { URLForm } from './useURLBuilderState';
import { toProgramLandingPageUrl } from '@client/helpers/routes';
import { ProductOption } from './useOtpProducts';

type URLData = {
  website: string;
  campaignName: string;
  campaignSource: string;
  collectionId?: number;
};

type SetProductionOptionFN = (
  product: ProductOption | { id: number; type: 'subscription' } | undefined,
) => void;

export const useURLBuilder = (influencerCustomUrl: LiteUser['customURL']) => {
  const [url, setUrl] = useState('');
  const isUrlGenerated = !!url;

  const [urlData, _setUrlData] = useState<URLData>(() => ({
    website: '',
    campaignName: '',
    campaignSource: '',
    collectionId: undefined,
  }));

  const setUrlData = <K extends keyof URLData>(key: K, value: URLData[K]) => {
    _setUrlData((prev) => ({ ...prev, [key]: value }));
  };

  const setProductionOption: SetProductionOptionFN = (product) => {
    setUrlData('collectionId', product?.id);
    setUrlData(
      'website',
      product
        ? toProductLandingPageURL(
            product.type === 'subscription'
              ? {
                  type: 'subscription',
                  product: { url: influencerCustomUrl },
                }
              : product.type === 'OFFER'
                ? {
                    type: 'offer',
                    product,
                  }
                : {
                    type: 'otp',
                    product,
                  },
          )
        : '',
    );
  };

  const createUrl = useCallback(() => {
    const params = new URLSearchParams({
      utm_source: urlData.campaignSource,
      utm_campaign: urlData.campaignName,
    });
    let v = `${urlData.website}?${params.toString()}`;
    if (!v.startsWith('http')) v = `https://${v}`;
    setUrl(v);
  }, [urlData]);

  useEffect(() => {
    if (isUrlGenerated) createUrl();
  }, [createUrl, isUrlGenerated]);

  return {
    url,
    urlData,
    setUrl,
    setUrlData,
    setProductionOption,
    createUrl,
    setData: (v: URLForm & { product: ProductOption | undefined }) => {
      setUrlData('campaignName', v.name);
      setUrlData('campaignSource', v.source);
      setProductionOption(
        v.productType === 'subscription'
          ? { type: 'subscription', id: -1 }
          : v.product,
      );
      createUrl();
    },
  };
};

type ProductArgs =
  | {
      type: 'offer';
      product: Pick<ProductOption, 'id' | 'name' | 'url'>;
    }
  | {
      type: 'otp';
      product: Pick<ProductOption, 'id' | 'name' | 'url'>;
    }
  | {
      type: 'subscription';
      product: { url: string };
    };

const toProductLandingPageURL = (a: ProductArgs) => {
  if (a.type === 'subscription')
    return `${import.meta.env.VITE_LANDING_PAGE_URL}/${a.product.url}`;

  if (a.type === 'offer') {
    return a.product.url;
  }

  const programUrl = toProgramLandingPageUrl({
    id: a.product.id,
    name: a.product.name ?? '_',
    influencer: { customURL: a.product.url },
  });

  return programUrl;
};
